import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cpu, HardDrive, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useMonitoringServers,
  useServerHealth,
  useCpuCurrent,
  useCpuHistory,
  useMemoryCurrent,
  useMemoryHistory,
  useContainerCpuHistory,
  useContainerMemoryHistory,
} from "@/hooks/useMonitoring";
import { TimeRange } from "@/types/monitoring";
import { MetricChart } from "./components/MetricChart";

export const MonitoringDashboardPage: React.FC = () => {
  const { serverId: routeServerId } = useParams<{ serverId: string }>();
  const { t } = useTranslation();

  const { data: servers } = useMonitoringServers();
  const [selectedServerId, setSelectedServerId] = useState<string>(
    routeServerId ?? "",
  );
  const [timeRange, setTimeRange] = useState<TimeRange>("1h");
  const [containerId, setContainerId] = useState("");
  const [activeContainerId, setActiveContainerId] = useState("");

  // Auto-select first server when list loads
  useEffect(() => {
    if (!routeServerId && !selectedServerId && servers?.length) {
      setSelectedServerId(servers[0].id);
    }
  }, [routeServerId, selectedServerId, servers]);

  const serverId = routeServerId || selectedServerId;

  const { data: health } = useServerHealth(serverId);
  const { data: cpuCurrent } = useCpuCurrent(serverId);
  const { data: memoryCurrent } = useMemoryCurrent(serverId);
  const { data: cpuHistory, isLoading: cpuHistoryLoading } = useCpuHistory(
    serverId,
    timeRange,
  );
  const { data: memoryHistory, isLoading: memoryHistoryLoading } =
    useMemoryHistory(serverId, timeRange);
  const { data: containerCpu, isLoading: containerCpuLoading } =
    useContainerCpuHistory(serverId, activeContainerId, timeRange);
  const { data: containerMemory, isLoading: containerMemoryLoading } =
    useContainerMemoryHistory(serverId, activeContainerId, timeRange);

  const selectedServer = servers?.find((s) => s.id === serverId);

  return (
    <div className="flex flex-col gap-6 p-4 md:p-8 max-w-[1344px] mx-auto">
      {/* Header with server selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold">
          {t("monitoring.dashboard.title")}
        </h1>
        <div className="flex items-center gap-2">
          {!routeServerId && (
            <Select value={serverId} onValueChange={setSelectedServerId}>
              <SelectTrigger
                size="sm"
                className="w-[180px] md:w-[220px] border-slate-200 shadow-xs"
              >
                <SelectValue
                  placeholder={t("monitoring.dashboard.selectServer")}
                />
              </SelectTrigger>
              <SelectContent>
                {servers?.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {serverId &&
            health &&
            (health.reachable ? (
              <Badge variant="verified">
                {t("monitoring.servers.connected")}
              </Badge>
            ) : (
              <Badge variant="new_destructive">
                {t("monitoring.servers.unreachable")}
              </Badge>
            ))}
        </div>
      </div>

      {/* Server info */}
      {selectedServer && (
        <p className="text-sm text-muted-foreground -mt-2">
          {selectedServer.url}
        </p>
      )}

      {!serverId && (
        <p className="text-sm text-muted-foreground py-8 text-center">
          {t("monitoring.dashboard.selectServer")}
        </p>
      )}

      {serverId && (
        <>
          {/* Time range selector */}
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">
              {t("monitoring.dashboard.timeRange")}
            </Label>
            <Select
              value={timeRange}
              onValueChange={(v) => setTimeRange(v as TimeRange)}
            >
              <SelectTrigger size="sm" className="w-[140px] border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">
                  {t("monitoring.dashboard.last1h")}
                </SelectItem>
                <SelectItem value="6h">
                  {t("monitoring.dashboard.last6h")}
                </SelectItem>
                <SelectItem value="24h">
                  {t("monitoring.dashboard.last24h")}
                </SelectItem>
                <SelectItem value="7d">
                  {t("monitoring.dashboard.last7d")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* CPU + Memory cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Cpu className="size-5 text-muted-foreground" />
                  <span className="font-semibold">
                    {t("monitoring.dashboard.cpuUsage")}
                  </span>
                </div>
                <span className="text-2xl font-bold text-royal-glow-3">
                  {cpuCurrent ? `${cpuCurrent.percent.toFixed(1)}%` : "—"}
                </span>
              </div>
              <MetricChart
                data={cpuHistory ?? []}
                color="var(--royal-glow-3)"
                unit="%"
                isLoading={cpuHistoryLoading}
              />
            </div>

            <div className="border rounded-lg p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HardDrive className="size-5 text-muted-foreground" />
                  <span className="font-semibold">
                    {t("monitoring.dashboard.memoryUsage")}
                  </span>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold text-skyline-2">
                    {memoryCurrent
                      ? `${memoryCurrent.usedPercent.toFixed(1)}%`
                      : "—"}
                  </span>
                  {memoryCurrent && (
                    <span className="text-xs text-muted-foreground">
                      {formatBytes(memoryCurrent.used)} /{" "}
                      {formatBytes(memoryCurrent.total)}
                    </span>
                  )}
                </div>
              </div>
              <MetricChart
                data={memoryHistory ?? []}
                color="var(--skyline-2)"
                unit="%"
                isLoading={memoryHistoryLoading}
              />
            </div>
          </div>

          {/* Container metrics */}
          <div className="border rounded-lg p-4 flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Server className="size-5 text-muted-foreground" />
              <span className="font-semibold">
                {t("monitoring.dashboard.containers")}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="flex flex-col gap-1.5 flex-1 sm:max-w-md">
                <Label htmlFor="containerId" className="text-xs">
                  {t("monitoring.dashboard.containerId")}
                </Label>
                <Input
                  id="containerId"
                  value={containerId}
                  onChange={(e) => setContainerId(e.target.value)}
                  placeholder={t("monitoring.dashboard.containerIdPlaceholder")}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    setActiveContainerId(containerId.trim())
                  }
                />
              </div>
              <Button
                variant="new_default"
                size="default"
                onClick={() => setActiveContainerId(containerId.trim())}
                disabled={!containerId.trim()}
              >
                {t("monitoring.dashboard.loadContainer")}
              </Button>
            </div>

            {activeContainerId ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium">
                    {t("monitoring.dashboard.cpuUsage")}
                  </span>
                  <MetricChart
                    data={containerCpu ?? []}
                    color="var(--royal-glow-3)"
                    unit="%"
                    isLoading={containerCpuLoading}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium">
                    {t("monitoring.dashboard.memoryUsage")}
                  </span>
                  <MetricChart
                    data={containerMemory ?? []}
                    color="var(--skyline-2)"
                    unit="%"
                    isLoading={containerMemoryLoading}
                  />
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t("monitoring.dashboard.noContainerData")}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
